import { ViaCep } from './../../../../../../../../shared/model/application';
import { ApplicationService } from './../../../../../../../../shared/services/application.service';
import { OemDealerLocation } from "./../../../../../../../../shared/services/oem-dealer.service";
import { MakeModel, OpenService } from "./../../../../../../../../shared/services/open.service";
import { City } from "./../../../../../../../../shared/model/city";
import { OemDealerLocationForm } from "./../../../../../model/advertisers";
import { AdminAdvertisersService } from "@app/views/private/admin/services/admin-advertisers.service";
import { Validators, FormControl, AbstractControl, ValidatorFn, FormGroupDirective } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { FormBuilder } from "@angular/forms";
import { FormGroup } from "@angular/forms";
import { Component, OnInit, OnDestroy, Inject, Output, EventEmitter, Input, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { UserInfo } from '@app/store/session/session.store';
import { SessionService } from '@app/store/session/session.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SegmentLabel } from '@app-model/vehicle';
import { SelectOption } from '@app-shared/components/ui/select-option.model';
import { ComboboxComponent } from '@app-shared/components/ui/combobox/combobox.component';
import { RegionalService } from '@app/views/private/admin/regionals/services/regional.service';

@Component({
  selector: "app-admin-create-location-modal",
  templateUrl: "./admin-create-location-modal.component.html",
  styleUrls: ["./admin-create-location-modal.component.scss"],
})
export class AdminCreateLocationModalComponent implements OnInit, OnDestroy {
  @Output()
  createNewLocation = new EventEmitter<OemDealerLocation>();

  @Output()
  backToList = new EventEmitter<any>();

  form: FormGroup;
  @ViewChild(FormGroupDirective) formGroupDirective: FormGroupDirective;

  location: OemDealerLocation;
  @Input()
  oemDealerId: string;

  cityOptions: SelectOption<string>[] = [];
  makeOptions: SelectOption<string>[] = [];
  regionalOptions: SelectOption<string | null>[] = [];

  private readonly citiesById = new Map<string, City>();
  private readonly makesById = new Map<string, MakeModel>();
  private readonly subscriptions = new Subscription();

  readonly cityIdControl = new FormControl<string | null>(null);
  readonly makeIdControl = new FormControl<string | null>(null);

  selectedCity: City;
  selectedMake: MakeModel;

  isEdit = false;

  userInfo: UserInfo;

  formReady = true;
  formSubmitted = false;

  constructor(
    private fb: FormBuilder,
    private adminAdvertisersService: AdminAdvertisersService,
    private toaster: ToastrService,
    private openService: OpenService,
    private applicationService: ApplicationService,
    private sessionService: SessionService,
    private regionalService: RegionalService,
    @Inject(MAT_DIALOG_DATA) public data: { oemDealerId: string }
  ) {
    this.oemDealerId = data.oemDealerId;
    this.initFormNew();
  }

  ngOnInit() {
    this.sessionService.getUser().subscribe((user) => {
      this.userInfo = user;
      if (this.canManageRegionalAar()) {
        this.loadRegionalOptions();
      }
    });

    this.subscriptions.add(
      this.cityIdControl.valueChanges.subscribe((cityId) => {
        this.selectedCity = cityId ? this.citiesById.get(cityId) : undefined;
      }),
    );

    this.subscriptions.add(
      this.makeIdControl.valueChanges.subscribe((makeId) => {
        this.selectedMake = makeId ? this.makesById.get(makeId) : undefined;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFieldError(controlName: string): string {
    if (!this.formSubmitted) {
      return '';
    }

    const control = this.form?.get(controlName);

    if (!control?.invalid) {
      if (controlName === 'cityId' && !this.cityIdControl.value) {
        return 'Selecione uma cidade válida da lista.';
      }
      return '';
    }

    const errors = control.errors;
    if (errors?.['required']) {
      return 'Campo obrigatório';
    }
    if (errors?.['cepInvalid']) {
      return 'CEP inválido';
    }

    return 'Campo inválido';
  }

  onCitySearch(query: string): void {
    const normalized = query?.trim() ?? '';
    if (!normalized) {
      this.cityOptions = [];
      return;
    }

    this.openService.searchCitiesByName(normalized).subscribe((cities) => {
      cities.forEach((city) => this.citiesById.set(city.id, city));
      this.cityOptions = cities.map((city) => ({
        value: city.id,
        label: `${city.name} - ${city.uf}`,
      }));
    });
  }

  onMakeSearch(query: string): void {
    const normalized = query?.trim() ?? '';
    if (!normalized) {
      this.makeOptions = [];
      return;
    }

    this.openService.getMakesByName(normalized).subscribe((makes) => {
      makes.forEach((make) => this.makesById.set(make.id, make));
      this.makeOptions = makes.map((make) => ({
        value: make.id,
        label: `${make.name} - ${this.getSegmentLabel(make.segment)}`,
      }));
    });
  }

  selectCity(city: City): void {
    this.citiesById.set(city.id, city);
    this.cityOptions = [{ value: city.id, label: `${city.name} - ${city.uf}` }];
    this.cityIdControl.setValue(city.id);
    this.selectedCity = city;
  }

  setMakeSelection(make: MakeModel): void {
    this.makesById.set(make.id, make);
    this.makeOptions = [{
      value: make.id,
      label: `${make.name} - ${this.getSegmentLabel(make.segment)}`,
    }];
    this.makeIdControl.setValue(make.id);
    this.selectedMake = make;
  }

  getSegmentLabel(value: string): string {
    return SegmentLabel.getSegment(value).vehicleLabel;
  }

  canManageRegionalAar(): boolean {
    return !!this.userInfo?.isPlatformAdmin();
  }

  private loadRegionalOptions(): void {
    this.regionalService.listSelectOptions(this.location?.regional ?? null).subscribe((options) => {
      this.regionalOptions = options;
    });
  }

  private getLocationRegionalAarId(location: OemDealerLocation = this.location): string | null {
    if (!location) {
      return null;
    }

    return location.regionalAarId
      ?? location.regional?.id
      ?? location.regionalAar
      ?? null;
  }

  private getSubmitRegionalAarId(formValue: string | null | undefined): string | null {
    if (this.canManageRegionalAar()) {
      return formValue ?? null;
    }

    if (this.isEdit) {
      return this.getLocationRegionalAarId();
    }

    return null;
  }

  isValidCEP(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      let cep: string = String(control.value);

      if (!cep) {
        return null;
      }
      cep = cep.replace("-", "").replace("_", "");
      if (cep.length < 8) {
        return { cepInvalid: true };
      }
      return null;
    };
  }

  initFormNew() {
    this.formSubmitted = false;
    this.selectedMake = undefined;
    this.selectedCity = undefined;
    this.cityOptions = [];
    this.makeOptions = [];
    this.citiesById.clear();
    this.makesById.clear();
    this.cityIdControl.setValue(null, { emitEvent: false });
    this.makeIdControl.setValue(null, { emitEvent: false });
    this.formReady = false;
    this.form = this.fb.group({
      id: [""],
      name: ["", [Validators.required]],
      address: ["", [Validators.required]],
      zipCode: ["", [this.isValidCEP(), Validators.required]],
      cityId: this.cityIdControl,
      makeId: this.makeIdControl,
      division: [""],
      addressNumber: ["", [Validators.required]],
      addressComplement: [""],
      regionalAarId: [null as string | null],
      sellingTerms: [""],
    });
    queueMicrotask(() => {
      this.formReady = true;
    });
  }

  clearForm() {
    this.initFormNew();
    this.formGroupDirective?.resetForm();
  }

  updateForm() {
    this.formSubmitted = false;
    const city = this.location.city as City;
    this.citiesById.set(city.id, city);
    this.cityOptions = [{ value: city.id, label: `${city.name} - ${city.uf}` }];

    if (this.canManageRegionalAar()) {
      this.loadRegionalOptions();
    }

    this.form.patchValue({
      id: this.location.id,
      name: this.location.name,
      address: this.location.address,
      zipCode: this.location.zipCode,
      cityId: city.id,
      makeId: null,
      division: this.location.division,
      addressNumber: this.location.addressNumber,
      addressComplement: this.location.addressComplement,
      regionalAarId: this.getLocationRegionalAarId(),
      sellingTerms: this.location.sellingTerms ?? "",
    });
    this.selectedCity = city;
    this.selectedMake = undefined;
    this.makeOptions = [];
    this.makeIdControl.setValue(null, { emitEvent: false });

    if (this.location.makeId) {
      this.openService.getMakes([this.location.makeId]).subscribe((makes) => {
        if (makes?.length) {
          this.setMakeSelection(makes[0]);
        }
        this.formReady = true;
      });
      return;
    }

    this.formReady = true;
  }

  markFormGroupTouched(formGroup: FormGroup) {
    (<any>Object).values(formGroup.controls).forEach((control: FormControl) => {
      control.markAsTouched();
    });
  }

  submit() {
    this.formSubmitted = true;
    this.markFormGroupTouched(this.form);
    this.cityIdControl.markAsTouched();
    if (!this.form.valid || !this.cityIdControl.value) {
      this.toaster.error("Verique os campos.");
      return;
    }

    (document.activeElement as HTMLElement | null)?.blur?.();

    const formValue = this.form.getRawValue();
    const loc = { ...formValue } as OemDealerLocationForm;
    loc.makeId = this.makeIdControl.value || (this.isEdit ? this.location.makeId : undefined);
    loc.cityId = this.cityIdControl.value;
    loc.regionalAarId = this.getSubmitRegionalAarId(formValue.regionalAarId) ?? '';

    if (this.isEdit) {
      loc.id = this.location.id;
    } else {
      loc.oemDealerId = this.oemDealerId;
      loc.id = null;
    }

    this.adminAdvertisersService
      .saveLocationOemDealers(loc)
      .subscribe((resp) => {
        resp.city = {
          name: this.selectedCity.name,
          uf: this.selectedCity.uf,
        };
        if (this.isEdit) {
          Object.assign(this.location, resp);
          const savedRegionalAarId = this.getLocationRegionalAarId(resp);
          this.location.regionalAarId = savedRegionalAarId;
          if (!savedRegionalAarId) {
            this.location.regional = null;
            this.location.regionalAar = null;
          }
        } else {
          this.createNewLocation.emit(resp);
        }
        this.toaster.success(this.isEdit ? 'Pátio atualizado com sucesso.' : 'Pátio criado com sucesso.');
        this.close();
      }, () => {
        this.toaster.error("Ocorreu um erro ao criar o patio.");
      });
  }

  reset() {
    this.selectedCity = null;
  }

  close() {
    this.reset();
    this.backToList.emit();
  }

  getCEP() {
    var cep = this.form.value.zipCode;
    if (
      cep == undefined ||
      cep.trim() == "" ||
      cep.trim().replace("-", "").length < 8
    ) {
      return;
    }

    this.applicationService
      .getCep(cep.trim().replace("-", ""))
      .subscribe((response: ViaCep) => {
        this.form.patchValue({
          address: response.logradouro,
        });
        this.openService.searchCitiesByName(response.localidade).subscribe(cityOptions => {
          const city = cityOptions.find(c => c.name === response.localidade && c.uf === response.uf);
          if (city) {
            this.selectCity(city);
          }
        });
      });
  }
}
